import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';

interface RecentActivity {
  id: string;
  timestamp: Date;
  userName: string;
  action: string;
  institutionName: string;
  status: 'SUCCESS' | 'FAILED' | 'PENDING';
}

interface SystemHealth {
  cpuUsage: number;
  memoryUsage: number;
  dbResponseTime: number;
}

interface SystemNotification {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: Date;
}

@Component({
  selector: 'app-admindashboard',
  templateUrl: './admindashboard.component.html',
  styleUrls: ['./admindashboard.component.scss']
})
export class AdmindashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Dashboard Stats
  totalUsers = 0;
  totalInstitutions = 0;
  systemAlerts = 0;
  lastBackupDays = 0;

  // System Health
  systemHealth: SystemHealth = {
    cpuUsage: 0,
    memoryUsage: 0,
    dbResponseTime: 0
  };

  // Recent Activities
  recentActivities = new MatTableDataSource<RecentActivity>([]);
  displayedColumns: string[] = ['timestamp', 'user', 'action', 'institution', 'status', 'actions'];

  // System Notifications
  systemNotifications: SystemNotification[] = [];

  constructor(
    private router: Router,
    // Inject your services here
    // private superAdminService: SuperAdminService,
    // private systemHealthService: SystemHealthService,
    // private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadDashboardData();
    this.startSystemHealthMonitoring();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDashboardData(): void {
    this.loadSystemStats();
    this.loadRecentActivities();
    this.loadSystemNotifications();
  }

  private loadSystemStats(): void {
    this.totalUsers = 1247;
    this.totalInstitutions = 23;
    this.systemAlerts = 3;
    this.lastBackupDays = 1;
  }

  private loadRecentActivities(): void {
 
    const mockActivities: RecentActivity[] = [
      {
        id: '1',
        timestamp: new Date(),
        userName: 'John Doe',
        action: 'Created new user account',
        institutionName: 'AUCDT Main Campus',
        status: 'SUCCESS'
      },
      {
        id: '2',
        timestamp: new Date(Date.now() - 3600000),
        userName: 'Jane Smith',
        action: 'Updated institution settings',
        institutionName: 'AUCDT Branch Campus',
        status: 'SUCCESS'
      },
      {
        id: '3',
        timestamp: new Date(Date.now() - 7200000),
        userName: 'Admin User',
        action: 'System backup initiated',
        institutionName: 'System Wide',
        status: 'PENDING'
      }
    ];
    this.recentActivities.data = mockActivities;
  }

  private loadSystemNotifications(): void {
  
    this.systemNotifications = [
      {
        id: '1',
        type: 'warning',
        title: 'High CPU Usage Detected',
        message: 'System CPU usage has exceeded 80% for the past 30 minutes.',
        timestamp: new Date()
      },
      {
        id: '2',
        type: 'info',
        title: 'Scheduled Maintenance',
        message: 'System maintenance is scheduled for tonight at 2:00 AM.',
        timestamp: new Date(Date.now() - 1800000)
      }
    ];
  }

  private startSystemHealthMonitoring(): void {
    interval(30000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateSystemHealth();
      });

    // Initial load
    this.updateSystemHealth();
  }

  private updateSystemHealth(): void {


    this.systemHealth = {
      cpuUsage: Math.floor(Math.random() * 100),
      memoryUsage: Math.floor(Math.random() * 100),
      dbResponseTime: Math.floor(Math.random() * 500) + 50
    };
  }

  navigateToUsers(): void {
    this.router.navigate(['/superadmin/users']);
  }

  navigateToInstitutions(): void {
    this.router.navigate(['/superadmin/institutions']);
  }

  navigateToSystemLogs(): void {
    this.router.navigate(['/superadmin/system-logs']);
  }

  navigateToBackups(): void {
    this.router.navigate(['/superadmin/backups']);
  }

  createNewInstitution(): void {
    this.router.navigate(['/superadmin/institutions/create']);
  }

  manageSystemSettings(): void {
    this.router.navigate(['/superadmin/system-settings']);
  }

  viewSystemReports(): void {
    this.router.navigate(['/superadmin/reports']);
  }

  manageUserRoles(): void {
    this.router.navigate(['/superadmin/user-roles']);
  }

  viewAuditLogs(): void {
    this.router.navigate(['/superadmin/audit-logs']);
  }

  systemMaintenance(): void {
    this.router.navigate(['/superadmin/maintenance']);
  }

  databaseBackup(): void {
    console.log('Initiating database backup...');
  }

  securityMonitoring(): void {
    this.router.navigate(['/superadmin/security']);
  }

  emergencyActions(): void {
    this.router.navigate(['/superadmin/emergency']);
  }

  // Table Methods
  viewActivityDetails(activity: RecentActivity): void {
    this.router.navigate(['/superadmin/activities', activity.id]);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'SUCCESS':
        return 'status-success';
      case 'FAILED':
        return 'status-failed';
      case 'PENDING':
        return 'status-pending';
      default:
        return '';
    }
  }

  getDatabaseHealthClass(): string {
    if (!this.systemHealth.dbResponseTime) return '';
    
    if (this.systemHealth.dbResponseTime < 100) return 'health-good';
    if (this.systemHealth.dbResponseTime < 300) return 'health-warning';
    return 'health-critical';
  }

  // Notification Methods
  getNotificationIcon(type: string): string {
    switch (type) {
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'success':
        return 'check_circle';
      default:
        return 'notifications';
    }
  }
}